import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Empty states get the graph-paper wash and a drawn glyph rather than a stock
 * illustration — it belongs to the same instrument language as the rest.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  compact = false,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
      className={cn(
        'relative flex flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed text-center',
        compact ? 'gap-2 px-6 py-8' : 'gap-3 px-8 py-16',
        className,
      )}
    >
      <div className="graph-paper pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_at_center,black,transparent_78%)]" />
      <div className="relative flex size-12 items-center justify-center rounded-xl bg-card shadow-card">
        <Icon className="size-5 text-muted-foreground" strokeWidth={1.6} />
      </div>
      <div className="relative space-y-1">
        <p className="text-[14px] font-bold tracking-tight">{title}</p>
        {description && (
          <p className="mx-auto max-w-sm text-xs leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {action && <div className="relative pt-1">{action}</div>}
    </motion.div>
  );
}
